/**
 * GetMeTo — `/get-me-to/:slug`.
 *
 * Reverse job search. A student picks a firm, tells us their grade
 * band + budget + country preference, and we hand back the specific
 * degrees that (a) actually feed graduates to that firm and (b) fit
 * the student's profile. This flips WUA's browse loop from
 * "pick a uni → hope you get a career" to "pick a career → get the
 * shortlist that leads there".
 *
 * The backend does all filtering + scoring (see `/api/get-me-to/{slug}`
 * in `routes/degrees.py`). We keep the frontend a thin form + table.
 */
import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ArrowLeft, ArrowRight, Target, TrendingUp, Wallet, Globe,
  Sparkles, AlertCircle, Trophy,
} from "lucide-react";
import PathwaySEO from "@/components/app/PathwaySEO";
import ChanceBadge from "@/components/app/ChanceBadge";

const GRADE_BANDS = [
  { key: "A*A*A*", label: "A*A*A* / IB 42+", hint: "Oxbridge / Ivy tier" },
  { key: "A*AA",   label: "A*AA / IB 39-41", hint: "Russell Group top" },
  { key: "AAA",    label: "AAA / IB 36-38",  hint: "Russell Group core" },
  { key: "AAB",    label: "AAB / IB 33-35",  hint: "Strong applicant" },
  { key: "ABB",    label: "ABB / IB 30-32",  hint: "Solid applicant" },
  { key: "BBB",    label: "BBB / IB 27-29",  hint: "Any uni bar" },
  { key: "any",    label: "Not sure yet",    hint: "Show all unis" },
];

const BUDGET_OPTIONS = [
  { key: 0,       label: "No budget cap" },
  { key: 60000,   label: "$60k total (3 yrs)"  },
  { key: 100000,  label: "$100k total (3 yrs)" },
  { key: 150000,  label: "$150k total (3 yrs)" },
  { key: 200000,  label: "$200k total (3 yrs)" },
  { key: 300000,  label: "$300k total (3 yrs)" },
];

const COUNTRY_OPTIONS = ["UK", "US", "Germany", "France", "Netherlands", "Ireland", "Canada", "Australia"];

// Map backend chance_bucket → the ChanceBadge component's expected bucket.
const BUCKET_ALIAS = {
  safety: "likely",
  balanced: "target",
  stretch: "ambitious",
  long_shot: "highly_ambitious",
};

// Pipeline-strength band vocabulary — same one used by EmployerFlow
// + /how-to-get-into/{firm} feeder table. We deliberately don't show
// the raw pct_of_cohort decimal because public destinations sources
// rarely support that precision.
const BAND_META = {
  strong:   { label: "Strong pipeline",   bg: "bg-emerald-100", text: "text-emerald-800", border: "border-emerald-300" },
  moderate: { label: "Regular hires",      bg: "bg-amber-100",   text: "text-amber-800",   border: "border-amber-300" },
  emerging: { label: "Emerging pipeline", bg: "bg-stone-100",   text: "text-stone-700",   border: "border-stone-300" },
  trace:    { label: "Occasional",         bg: "bg-stone-50",    text: "text-stone-500",   border: "border-stone-200" },
};

export default function GetMeTo() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();

  // Form state — initialised from URL params so shared links deep-link.
  const [gradeBand, setGradeBand] = useState(params.get("grade") || "AAA");
  const [budget, setBudget] = useState(Number(params.get("budget") || 0));
  const [countries, setCountries] = useState(
    (params.get("countries") || "").split(",").filter(Boolean),
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  // Sync current filters back to URL so the family can share their
  // exact filtered view (helpful for parent/child collab).
  useEffect(() => {
    const p = new URLSearchParams();
    if (gradeBand && gradeBand !== "AAA") p.set("grade", gradeBand);
    if (budget) p.set("budget", String(budget));
    if (countries.length) p.set("countries", countries.join(","));
    setParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gradeBand, budget, countries]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(null);
    const q = new URLSearchParams({
      grade_band: gradeBand,
      budget_usd: String(budget),
      countries: countries.join(","),
      limit: "25",
    });
    api.get(`/get-me-to/${encodeURIComponent(slug)}?${q.toString()}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setErr("network"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug, gradeBand, budget, countries]);

  const firmName = data?.firm_name || slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  const matches = data?.matches || [];
  const dropped = data?.dropped_by || [];

  const toggleCountry = (c) => {
    setCountries((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);
  };

  const summaryLine = useMemo(() => {
    if (!data) return "";
    const n = matches.length;
    const total = data.total_feeder || 0;
    if (n === 0 && total > 0) {
      return `We know of ${total} unis that feed ${firmName} — but none pass your filters yet. Loosen a filter below to see them.`;
    }
    if (n === 0) return `We don't yet have destinations data linking any uni to ${firmName}.`;
    return `${n} of ${total} ${firmName} feeder unis fit your profile.`;
  }, [data, matches.length, firmName]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10" data-testid="get-me-to-page">
      <PathwaySEO
        title={`Get me to ${firmName} — which uni + degree gets me hired?`}
        description={`Pick your grade band, budget and country preference. We rank the universities whose graduates actually land at ${firmName} against your real applicant profile.`}
        path={`/get-me-to/${slug}`}
      />

      <Link
        to={`/how-to-get-into/${slug}`}
        data-testid="get-me-to-back"
        className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-[#4C1D95]"
      >
        <ArrowLeft className="h-3 w-3" /> Full {firmName} guide
      </Link>

      {/* HERO */}
      <div className="mt-6">
        <div className="label-uppercase text-[#4C1D95] flex items-center gap-1.5">
          <Trophy className="h-3 w-3" /> Reverse job search
        </div>
        <h1
          data-testid="get-me-to-heading"
          className="mt-2 font-editorial text-4xl sm:text-5xl leading-tight text-[#002147]"
        >
          Get me to {firmName}
        </h1>
        <p className="mt-3 text-stone-700 leading-relaxed max-w-3xl">
          Tell us your grade band and budget. We'll show you only the
          universities whose graduates <em>actually</em> land at{" "}
          {firmName} — and only the ones you can realistically apply
          to. Everything below is sourced from graduate destinations
          surveys; nothing is invented.
        </p>
      </div>

      {/* FILTERS */}
      <section
        data-testid="get-me-to-filters"
        className="mt-8 rounded-lg border border-stone-200 bg-white p-5 grid md:grid-cols-3 gap-5"
      >
        <div>
          <label className="label-uppercase text-stone-600 flex items-center gap-1.5">
            <Target className="h-3 w-3 text-[#4C1D95]" /> Your grade band
          </label>
          <select
            data-testid="get-me-to-grade-select"
            value={gradeBand}
            onChange={(e) => setGradeBand(e.target.value)}
            className="mt-2 w-full px-3 py-2 rounded-md border border-stone-300 bg-white text-sm focus:outline-none focus:border-[#4C1D95] focus:ring-1 focus:ring-[#4C1D95]"
          >
            {GRADE_BANDS.map((g) => (
              <option key={g.key} value={g.key}>{g.label}</option>
            ))}
          </select>
          <p className="mt-1.5 text-[11px] text-stone-500">
            {GRADE_BANDS.find((g) => g.key === gradeBand)?.hint}
          </p>
        </div>

        <div>
          <label className="label-uppercase text-stone-600 flex items-center gap-1.5">
            <Wallet className="h-3 w-3 text-[#4C1D95]" /> Total 3-year budget
          </label>
          <select
            data-testid="get-me-to-budget-select"
            value={budget}
            onChange={(e) => setBudget(Number(e.target.value))}
            className="mt-2 w-full px-3 py-2 rounded-md border border-stone-300 bg-white text-sm focus:outline-none focus:border-[#4C1D95] focus:ring-1 focus:ring-[#4C1D95]"
          >
            {BUDGET_OPTIONS.map((b) => (
              <option key={b.key} value={b.key}>{b.label}</option>
            ))}
          </select>
          <p className="mt-1.5 text-[11px] text-stone-500">
            International tuition × course duration. Living costs not included.
          </p>
        </div>

        <div>
          <label className="label-uppercase text-stone-600 flex items-center gap-1.5">
            <Globe className="h-3 w-3 text-[#4C1D95]" /> Country preference
          </label>
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="get-me-to-country-chips">
            {COUNTRY_OPTIONS.map((c) => {
              const active = countries.includes(c);
              return (
                <button
                  type="button"
                  key={c}
                  data-testid={`get-me-to-country-${c}`}
                  onClick={() => toggleCountry(c)}
                  className={`text-[11px] font-underground-sc tracking-wider px-2.5 py-1 rounded-full border transition-colors ${
                    active
                      ? "bg-[#4C1D95] text-white border-[#4C1D95]"
                      : "bg-white text-stone-700 border-stone-300 hover:border-[#4C1D95]"
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>
          <p className="mt-1.5 text-[11px] text-stone-500">
            Click chips to include. Empty = all countries.
          </p>
        </div>
      </section>

      {/* SUMMARY */}
      <div className="mt-6 text-[13px] font-underground-sc tracking-wider text-stone-500" data-testid="get-me-to-summary">
        {loading ? "Loading matches…" : (err ? "Couldn't load. Retry." : summaryLine)}
      </div>

      {/* TOP-5 MINI FLOW — the strongest {firm} pipelines that survive
          the family's filters. Same visual language as /how-to-get-into
          so someone flipping between the two pages sees a consistent
          "strongest pipelines at a glance" chip row. */}
      {matches.length > 0 && (
        <section
          data-testid="get-me-to-mini-flow"
          className="mt-4 rounded-lg border border-stone-200 bg-white p-5"
        >
          <div className="flex items-center gap-2">
            <Trophy className="h-4 w-4 text-[#4C1D95]" />
            <div className="label-uppercase text-[#4C1D95]">Top matches at a glance</div>
          </div>
          <p className="mt-1.5 text-[12px] font-underground-sc tracking-wider text-stone-500">
            The 5 strongest {firmName} pipelines that fit your profile
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {matches.slice(0, 5).map((m, i) => {
              const band = BAND_META[m.strength_band] || BAND_META.emerging;
              return (
                <Link
                  key={`mini-${m.university_id}-${m.subject_area}-${i}`}
                  to={`/uni/${m.university_id}`}
                  data-testid={`get-me-to-mini-flow-chip-${i}`}
                  className={`inline-flex items-center gap-2 rounded-md border ${band.border} ${band.bg} px-3 py-1.5 hover:shadow-sm transition-shadow`}
                >
                  <span className="font-mono text-[11px] text-stone-500 tabular-nums">
                    #{i + 1}
                  </span>
                  <span className="font-medium text-sm text-[#002147]">
                    {m.university_name || m.university_id}
                  </span>
                  <span className="text-[11px] font-mono text-stone-500">
                    · {m.subject_area}
                  </span>
                  <span className={`text-[9px] font-underground-sc tracking-wider ${band.text}`}>
                    {band.label}
                  </span>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* MATCHES TABLE */}
      {matches.length > 0 && (
        <section className="mt-4 rounded-lg border border-stone-200 overflow-x-auto" data-testid="get-me-to-matches">
          <table className="w-full text-[13px] border-collapse">
            <thead>
              <tr className="text-left border-b border-stone-200 bg-stone-50 font-underground-sc text-[10px] text-stone-500 tracking-wider">
                <th className="py-2.5 px-3">#</th>
                <th className="py-2.5 px-3">University</th>
                <th className="py-2.5 px-3">Subject</th>
                <th className="py-2.5 px-3">Signal</th>
                <th className="py-2.5 px-3">Your chance</th>
                <th className="py-2.5 px-3 text-right hidden sm:table-cell">3-yr cost</th>
              </tr>
            </thead>
            <tbody>
              {matches.map((m, i) => (
                <tr
                  key={`${m.university_id}-${m.subject_area}`}
                  data-testid={`get-me-to-match-row-${i}`}
                  className="border-b border-stone-100 hover:bg-stone-50/60"
                >
                  <td className="py-2.5 px-3 font-mono text-stone-500">{i + 1}</td>
                  <td className="py-2.5 px-3">
                    <Link to={`/uni/${m.university_id}`} className="text-[#4C1D95] hover:underline font-medium">
                      {m.university_name || m.university_id}
                    </Link>
                    {m.country && (
                      <span className="ml-2 text-[11px] font-mono text-stone-500">· {m.country}</span>
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-stone-700">{m.subject_area || "—"}</td>
                  <td className="py-2.5 px-3">
                    {(() => {
                      const band = BAND_META[m.strength_band] || BAND_META.emerging;
                      return (
                        <span
                          data-testid={`get-me-to-match-row-${i}-band`}
                          className={`inline-block text-[10px] font-underground-sc tracking-wider ${band.bg} ${band.text} border ${band.border} rounded-full px-2 py-[2px]`}
                        >
                          {band.label}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="py-2.5 px-3">
                    <ChanceBadge bucket={BUCKET_ALIAS[m.chance_bucket] || "target"} size="sm" />
                  </td>
                  <td className="py-2.5 px-3 text-right hidden sm:table-cell font-mono text-stone-600">
                    {m.estimated_3yr_cost_usd
                      ? `$${(m.estimated_3yr_cost_usd / 1000).toFixed(0)}k`
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* EMPTY STATE */}
      {!loading && matches.length === 0 && (
        <div
          data-testid="get-me-to-empty"
          className="mt-4 rounded-md border border-amber-300 bg-amber-50/60 p-4 text-[13px] text-stone-700 flex items-start gap-2"
        >
          <AlertCircle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
          <div>
            {data?.total_feeder === 0 ? (
              <>
                We don't yet have destinations data linking any uni to{" "}
                <span className="font-semibold">{firmName}</span>. Try one of the
                related firms on the{" "}
                <Link to={`/how-to-get-into/${slug}`} className="text-[#4C1D95] underline">full firm guide</Link>.
              </>
            ) : (
              <>
                No unis pass your filters. Loosen your grade band, raise your
                budget, or add more countries to see the {data?.total_feeder}{" "}
                feeder unis we found.
              </>
            )}
          </div>
        </div>
      )}

      {/* DROPPED — transparency about what was filtered out */}
      {dropped.length > 0 && (
        <details
          data-testid="get-me-to-dropped-panel"
          className="mt-6 rounded-lg border border-stone-200 bg-stone-50/60"
        >
          <summary className="cursor-pointer px-4 py-3 text-[13px] font-underground-sc tracking-wider text-stone-600 hover:text-[#4C1D95]">
            {dropped.length} unis dropped by your filters — show me why
          </summary>
          <div className="px-4 pb-4 border-t border-stone-200">
            <ul className="mt-3 space-y-2 text-[12px] text-stone-700">
              {dropped.slice(0, 12).map((m) => (
                <li key={`${m.university_id}-${m.subject_area}`} className="flex items-start gap-2">
                  <span className="shrink-0 text-stone-400 font-mono">·</span>
                  <div className="min-w-0">
                    <Link to={`/uni/${m.university_id}`} className="text-[#4C1D95] hover:underline font-medium">
                      {m.university_name || m.university_id}
                    </Link>
                    <span className="text-stone-500"> · {m.subject_area}</span>
                    <div className="text-[11px] text-amber-700 mt-0.5">
                      {(m.drop_reasons || []).join(" · ")}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </details>
      )}

      {/* CTA to Advanced Search with career-boost */}
      <section className="mt-10 rounded-lg border border-emerald-300 bg-emerald-50/40 p-5">
        <div className="flex items-center gap-2 text-emerald-800">
          <Sparkles className="h-4 w-4" />
          <div className="label-uppercase">Want your full personal shortlist?</div>
        </div>
        <p className="mt-2 text-[14px] text-stone-700 leading-relaxed">
          Advanced Search lets you add your other filters (visa route,
          teaching style, city vibe, satisfaction score) on top of the{" "}
          {firmName} pipeline boost — so you get a properly balanced
          shortlist, not just the top-cohort names.
        </p>
        <Link
          to={`/advanced?dream_employer=${encodeURIComponent(firmName)}&boost_career=1`}
          data-testid="get-me-to-cta-advanced"
          className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-[#4C1D95] px-4 py-2 text-white text-sm font-medium hover:bg-[#3B0764]"
        >
          Build my full shortlist with {firmName} pipeline boosted <ArrowRight className="h-4 w-4" />
        </Link>
      </section>

      {/* Footer nav */}
      <div className="mt-10 text-[12px] text-stone-500 flex flex-wrap items-center gap-x-3 gap-y-2">
        <TrendingUp className="h-3 w-3" />
        <span>Also see:</span>
        <Link to={`/how-to-get-into/${slug}`} className="text-[#4C1D95] underline">
          Full {firmName} entry guide
        </Link>
        <span>·</span>
        <Link to="/firm-guides" className="text-[#4C1D95] underline">All firm guides</Link>
        <span>·</span>
        <Link to="/methodology" className="text-[#4C1D95] underline">Data methodology</Link>
      </div>
    </div>
  );
}
